import React from "react";
import { Card, Title, Text, Button, Group, Stack, Badge, Table, Loader } from "@mantine/core";
import { IconPlus, IconEdit } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { RoleGuard } from "./RoleGuard";
import { useMeeting, useAwardSheetsByMeeting } from "../hooks";
import { filterVotingMembers } from "@hmh-proc/shared";

interface AwardSheetTabProps {
  meetingId: string;
}

export const AwardSheetTab: React.FC<AwardSheetTabProps> = ({ meetingId }) => {
  const navigate = useNavigate();
  const { data: meeting, isLoading: meetingLoading } = useMeeting(meetingId);
  const { data: awardSheets, isLoading: awardSheetsLoading } = useAwardSheetsByMeeting(meetingId);

  const hasVotingMembers = filterVotingMembers(meeting?.attendances ?? []).length > 0;
  const hasPresentWithoutVote =
    (meeting?.attendances?.some((att) => att.presence) || false) &&
    !hasVotingMembers;

  // Draft award sheets can be created anytime before cancellation; multiple sheets per meeting are allowed.
  const canCreateAwardSheet =
    meeting != null && meeting.status !== "CANCELLED";

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "MVR",
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      DRAFT: 'gray',
      PENDING: 'yellow',
      APPROVED: 'green',
      REJECTED: 'red',
    };
    return <Badge color={colors[status] || 'gray'}>{status}</Badge>;
  };

  if (meetingLoading || awardSheetsLoading) {
    return (
      <Card>
        <Stack align="center" py="xl">
          <Loader size="md" />
          <Text c="dimmed">Loading award sheets...</Text>
        </Stack>
      </Card>
    );
  }

  return (
    <Card>
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={3}>Award Sheets</Title>
          {canCreateAwardSheet && (
            <RoleGuard allowedRoles={["PROCUREMENT_OFFICER", "HEAD_OF_PROCUREMENT"]}>
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={() =>
                  navigate(
                    `/bid-committee/award-sheets/new?meetingId=${meetingId}`
                  )
                }
              >
                New award sheet
              </Button>
            </RoleGuard>
          )}
        </Group>

        {!awardSheets || awardSheets.length === 0 ? (
          <Stack gap="md" align="center" py="xl">
            <Text c="dimmed" size="lg">
              No award sheets have been created for this meeting yet.
            </Text>
            {!canCreateAwardSheet && (
              <Text size="sm" c="dimmed">
                Award sheets cannot be added to a cancelled meeting.
              </Text>
            )}
            {canCreateAwardSheet && hasPresentWithoutVote && (
              <Text size="sm" c="dimmed">
                You can prepare draft award sheets now. Enable Allow Vote on at least one present member before sending for approval.
              </Text>
            )}
            {canCreateAwardSheet && !hasVotingMembers && !hasPresentWithoutVote && (
              <Text size="sm" c="dimmed">
                You can prepare draft award sheets now. Mark attendance and enable Allow Vote before sending for approval.
              </Text>
            )}
          </Stack>
        ) : (
          <Stack gap="sm">
            <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Award Sheet Number</Table.Th>
                <Table.Th>Agenda items</Table.Th>
                <Table.Th>Tender Name</Table.Th>
                <Table.Th>Vendor</Table.Th>
                <Table.Th>Cost</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {awardSheets.map((sheet) => (
                <Table.Tr key={sheet.id}>
                  <Table.Td>{sheet.awardSheetNumber}</Table.Td>
                  <Table.Td>
                    {sheet.agendaLinks?.length
                      ? sheet.agendaLinks
                          .map((l) => l.agendaItem?.subject ?? '—')
                          .join(', ')
                      : '—'}
                  </Table.Td>
                  <Table.Td>{sheet.tenderName || '—'}</Table.Td>
                  <Table.Td>{sheet.vendor}</Table.Td>
                  <Table.Td>{formatCurrency(Number(sheet.proposedCost))}</Table.Td>
                  <Table.Td>{getStatusBadge(sheet.status)}</Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Button
                        variant="subtle"
                        size="xs"
                        onClick={() => navigate(`/bid-committee/award-sheets/${sheet.id}`)}
                      >
                        View
                      </Button>
                      {sheet.status === 'DRAFT' && (
                        <Button
                          variant="subtle"
                          size="xs"
                          leftSection={<IconEdit size={14} />}
                          onClick={() => navigate(`/bid-committee/award-sheets/${sheet.id}/edit`)}
                        >
                          Edit
                        </Button>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
            {canCreateAwardSheet && hasPresentWithoutVote && (
              <Text size="sm" c="dimmed">
                Enable Allow Vote on at least one present member before sending any sheet for approval.
              </Text>
            )}
            {canCreateAwardSheet && !hasVotingMembers && !hasPresentWithoutVote && (
              <Text size="sm" c="dimmed">
                Mark attendance and enable Allow Vote before sending any sheet for approval.
              </Text>
            )}
          </Stack>
        )}
      </Stack>
    </Card>
  );
};
